import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { clearActiveApp, getActiveApp, setActiveApp } from '../utils/appStorage';

const AppContext = createContext(undefined);

export const AppProvider = ({ children }) => {
  const [activeApp, setActiveAppState] = useState(() => getActiveApp());

  const refreshActiveApp = useCallback(() => {
    setActiveAppState(getActiveApp());
  }, []);

  const selectApp = useCallback((app) => {
    setActiveAppState(app || null);
    setActiveApp(app || null);
  }, []);

  const clearApp = useCallback(() => {
    setActiveAppState(null);
    clearActiveApp();
  }, []);

  useEffect(() => {
    const onStorage = () => refreshActiveApp();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshActiveApp]);

  const value = useMemo(
    () => ({
      activeApp,
      selectApp,
      clearApp,
      refreshActiveApp,
      hasActiveApp: Boolean(activeApp),
    }),
    [activeApp, selectApp, clearApp, refreshActiveApp]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useActiveApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useActiveApp must be used within an AppProvider');
  }
  return ctx;
};

